/* @jsx jsx */
/* @jsxFrag React.Fragment */
import * as React from 'react';

import { jsx } from '@emotion/react';
import styled from '@emotion/styled';

import { COLORS, SnackbarProvider, mq } from '@clutter/clean';

import { Footer } from '@shared/content/footer';
import { CustomLink } from '@shared/custom_link';

import { ContactFormComponent } from './partnerships/contact_form';
import { ConversationComponent } from './partnerships/conversation';
import { FAQsComponent } from './partnerships/faqs';
import { HeroSectionComponent } from './partnerships/hero_section';
import { HowItWorksComponent } from './partnerships/how_it_works';
import { LearnMoreComponent } from './partnerships/learn_more';

// Clutter logo SVG component
const ClutterLogoSVG: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="119"
    height="28"
    viewBox="0 0 119 28"
    fill="none"
    className={className}
  >
    <path
      d="M94.594 23.5474C97.0123 23.6645 99.3564 22.6978 100.985 20.9122L102.968 24.1514C101.15 26.018 98.3403 27.5553 93.9329 27.5553C88.8093 27.5553 84.6222 24.371 84.6222 17.4533C84.6222 10.9749 88.4236 7.24157 94.4287 7.24157C100.599 7.24157 104.472 10.9694 103.188 18.5514H90.2417C90.5722 21.7906 92.0046 23.5474 94.594 23.5474ZM94.594 11.0298C92.0597 11.0298 90.6824 12.6769 90.3518 15.6416H98.5056C98.5606 12.5671 97.1833 11.0353 94.594 11.0353V11.0298ZM26.6097 27.1216V0.823529L32.0088 0V27.1216H26.6097ZM48.5366 20.8627V7.57647H53.8806V27.1216H48.5366V24.7608C46.7736 25.9686 44.019 27.5059 40.8787 27.5059C36.6366 27.5059 35.0389 24.9804 35.0389 21.3569V7.57647H40.438V20.149C40.438 21.9608 40.9338 23.3333 42.9722 23.3333C45.0106 23.3333 47.3245 21.9608 48.5366 20.8627ZM79.719 22.8941C80.7696 22.7644 81.7926 22.4678 82.7491 22.0157L84.6773 25.9686C82.7108 26.9023 80.5735 27.4255 78.3968 27.5059C74.6505 27.5059 72.7222 25.4745 72.7222 20.9176V11.5294H64.4583V21.0275C64.4583 22.6745 65.2847 22.8941 66.1111 22.8941C67.0477 22.8941 69.0861 22.0706 69.5819 21.851L71.5102 25.8039C70.2981 26.3529 67.3782 27.5059 64.7889 27.5059C61.0426 27.5059 59.1143 25.4745 59.1143 20.9176V11.5294H56.4148V7.57647H59.1143V2.7451L64.4583 1.97647V7.57647H72.7222V2.7451L78.0662 1.97647V7.57647H84.6222C84.8977 7.57647 85.063 7.68627 84.8977 8.07059C84.7324 8.4549 83.2449 11.5294 83.2449 11.5294H78.0662V21.0275C78.0662 22.6745 78.8926 22.8941 79.719 22.8941ZM111.673 11.6941C112.87 8.78605 115.869 7.03328 119 7.41176V12.4078C112.444 12.4078 111.673 18.3373 111.673 18.6667V27.1216H106.274V8.4L111.673 7.57647V11.6941ZM14.269 22.4C18.3458 22.4 21.6514 18.7765 21.6514 18.8314L24.0755 22.5647C20.4595 27.0042 14.4299 28.6935 9.02227 26.782C3.6146 24.8706 0.000752255 19.7728 0 14.0549C0 9.00392 3.69671 0.603922 13.6079 0.603922C20.5 0.603922 23.1389 4.33176 23.1389 6.14902C23.1555 7.11501 22.7777 8.04629 22.0922 8.72945C21.4066 9.41261 20.4721 9.78907 19.5028 9.77255C16.4562 9.71216 15.5361 6.92314 15.9769 5.7098C15.3926 5.63123 14.8034 5.59453 14.2139 5.6C11.9466 5.55979 9.75945 6.43585 8.15078 8.02862C6.54211 9.62138 5.64887 11.7952 5.67454 14.0549C5.70065 16.2983 6.62158 18.4391 8.23403 20.0047C9.84649 21.5704 12.0179 22.4322 14.269 22.4Z"
      fill="#097E77"
    />
  </svg>
);

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafb 0%, #ffffff 100%);
`;

const CustomHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: ${COLORS.cloud};
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  ${mq({
    padding: ['12px 20px', '16px 24px', '20px 32px'],
  })}
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
`;

const Logo = styled(ClutterLogoSVG)`
  height: 32px;
  width: auto;

  ${mq({
    height: ['28px', '32px', '36px'],
  })}
`;

const GetStartedButton = styled.button`
  background: ${COLORS.tealPrimary};
  color: ${COLORS.cloud};
  border: none;
  border-radius: 24px;
  padding: 12px 24px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  display: inline-block;

  &:hover {
    background: ${COLORS.tealJungle};
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(9, 126, 119, 0.3);
  }

  ${mq({
    display: ['none', 'inline-block'], // Hide on mobile, show on larger screens
    padding: ['10px 20px', '12px 24px', '14px 28px'],
    fontSize: ['13px', '14px', '15px'],
  })}
`;

const HeaderRightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 30px;
  height: 20px;
  position: relative;

  &:hover {
    opacity: 0.7;
  }

  ${mq({
    display: ['flex', 'none'], // Show on mobile, hide on larger screens
  })}
`;

const MenuDropdown = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  background: ${COLORS.cloud};
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  padding: 8px 0;
  min-width: 200px;
  z-index: 1000;
  display: ${(props) => (props.isOpen ? 'block' : 'none')};
  margin-top: 8px;
`;

const MenuDropdownItem = styled.button`
  width: 100%;
  padding: 12px 20px;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  font-size: 14px;
  color: ${COLORS.tealDark};
  transition: background-color 0.2s ease;

  &:hover {
    background: rgba(117, 117, 117, 0.1);
  }
`;

const MenuContainer = styled.div`
  position: relative;
`;

const MenuLine = styled.div`
  width: 100%;
  height: 2px;
  background: ${COLORS.tealDark};
  border-radius: 2px;
  transition: 0.3s;
`;

export const Partnerships: React.FC = () => {
  const contactFormRef = React.useRef<HTMLDivElement>(null);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  const scrollToContactForm = () => {
    contactFormRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
    setIsMenuOpen(false); // Close menu after navigation
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isMenuOpen &&
        !(event.target as Element)?.closest('[data-menu-container]')
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  return (
    <SnackbarProvider>
      <Container>
        <CustomHeader>
          <LogoContainer>
            <CustomLink to="/">
              <Logo />
            </CustomLink>
          </LogoContainer>
          <HeaderRightSection>
            <GetStartedButton onClick={scrollToContactForm}>
              Get started
            </GetStartedButton>
            <MenuContainer data-menu-container>
              <MenuButton
                onClick={toggleMenu}
                aria-label="Main menu"
                aria-expanded={isMenuOpen}
                aria-controls="main-menu-dropdown"
              >
                <MenuLine />
                <MenuLine />
                <MenuLine />
              </MenuButton>
              <MenuDropdown
                id="main-menu-dropdown"
                isOpen={isMenuOpen}
                role="menu"
              >
                <MenuDropdownItem onClick={scrollToContactForm} role="menuitem">
                  Get Started
                </MenuDropdownItem>
                <MenuDropdownItem
                  role="menuitem"
                  onClick={() => {
                    window.location.href = '/';
                  }}
                >
                  Home
                </MenuDropdownItem>
              </MenuDropdown>
            </MenuContainer>
          </HeaderRightSection>
        </CustomHeader>
        <HeroSectionComponent />
        <HowItWorksComponent />
        <LearnMoreComponent />
        <div ref={contactFormRef} id="contact">
          <ContactFormComponent />
        </div>
        <FAQsComponent />
        <ConversationComponent />
        <Footer />
      </Container>
    </SnackbarProvider>
  );
};
